import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useSite } from './context';
import { Busy, Failure, Notice } from './bits';
import { Identify } from './Identify';
import { money } from './time';
import { asFailure } from '../api/errors';
import type { Booking } from '../wire';

/**
 * "My visits" — the one list this surface has, and the reason it is allowed.
 *
 * `GET /api/bookings` is bounded by the second RLS key (`app.client_id`) before
 * the planner sees a row, not by a `WHERE` somebody remembered and not by a
 * parameter this page could change. There is no client id in the request because
 * there is nowhere to put one: the session *is* the identity. That is the same
 * structural move the partner surface makes in §5.1, and it is why a screen
 * called "my visits" is not a data browser.
 *
 * Cancelling is not deleting (spec §7.3): the appointment survives with
 * `status = 'cancelled'`, its unit rows fall out of the exclusion constraint,
 * and the slot returns to the book — for the partner's agent as much as for this
 * page, because there is one book.
 *
 * The fee is always zero today and the screen does not promise otherwise: it
 * says what the server said. Cancellation policy — notice windows, deposits, a
 * charge — arrives with MVP 4's money surfaces, and inventing a "free if 24h
 * ahead" line here would be this bundle deciding a tenant's policy.
 */

export function Visits(): ReactNode {
  const { api, site, signedIn, expire } = useSite();
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await api.bookings();
      setBookings(answer.bookings);
    } catch (e) {
      if (asFailure(e).code === 'unauthenticated') {
        expire();
        setBookings(null);
        return;
      }
      setError(e);
    } finally {
      setBusy(false);
    }
  }, [api, expire]);

  useEffect(() => {
    if (signedIn) void load();
  }, [signedIn, load]);

  if (!signedIn) {
    return (
      <section className="visits">
        <h2 className="visits__title">Your visits</h2>
        <Identify
          purpose="Sign in with your mobile number or email to see your upcoming visits."
          onSignedIn={() => void load()}
        />
      </section>
    );
  }

  async function cancel(booking: Booking): Promise<void> {
    setCancelling(booking.booking_id);
    setError(null);
    try {
      const answer = await api.cancelBooking(booking.booking_id);
      setBookings((current) =>
        (current ?? []).map((b) =>
          b.booking_id === answer.booking_id ? { ...b, status: answer.status } : b,
        ),
      );
    } catch (e) {
      if (asFailure(e).code === 'unauthenticated') {
        expire();
        return;
      }
      setError(e);
    } finally {
      setCancelling(null);
    }
  }

  return (
    <section className="visits">
      <h2 className="visits__title">Your visits</h2>

      {busy && bookings === null ? <Busy label="Loading your visits…" /> : null}
      {error === null ? null : <Failure error={error} />}

      {bookings !== null && bookings.length === 0 ? (
        <Notice title="Nothing booked yet.">
          <p className="notice__hint">Anything you book at {site.name} will show up here.</p>
        </Notice>
      ) : null}

      <ul className="visits__list">
        {(bookings ?? []).map((booking) => (
          <VisitRow
            key={booking.booking_id}
            booking={booking}
            busy={cancelling === booking.booking_id}
            onCancel={() => void cancel(booking)}
          />
        ))}
      </ul>
    </section>
  );
}

function VisitRow({
  booking,
  busy,
  onCancel,
}: {
  booking: Booking;
  busy: boolean;
  onCancel: () => void;
}): ReactNode {
  const { site, clock } = useSite();
  const cancelled = booking.status === 'cancelled';

  return (
    <li className={`visit ${cancelled ? 'visit--cancelled' : ''}`}>
      <div className="visit__what">
        <strong>{booking.service_name}</strong>
        <p className="visit__when">{clock.slot(booking.start, booking.end)}</p>
        <p className="visit__meta">
          {money(booking.value_cents, site.currency)}
          {booking.party_size > 1 ? ` · ${String(booking.party_size)} people` : ''}
          {/* Anything other than `confirmed` or `cancelled` is printed as it
              came: `guest-api` passes `appointments.status` straight through and
              a guest must not meet a blank row because the book learned a word
              this bundle predates. */}
          {cancelled || booking.status === 'confirmed' ? '' : ` · ${booking.status}`}
        </p>
      </div>

      {cancelled ? (
        <span className="visit__status">Cancelled</span>
      ) : (
        <button className="button button--quiet" type="button" disabled={busy} onClick={onCancel}>
          {busy ? 'Cancelling…' : 'Cancel'}
        </button>
      )}
    </li>
  );
}
